import { useStore } from 'app/context';
import { UsageSlide, UsageSource } from './UsageReportShared';
import { formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useState } from 'react';

interface MatomoVisitsData {
	totalVisits: number;
	devices: { label: string; nb_visits: number }[];
	averageTime: number;
	actionsPerVisit: number;
	pageViews: number;
}

const formatDuration = (seconds: number): string => {
	const min = Math.floor(seconds / 60);
	const sec = Math.round(seconds % 60);
	return min > 0 ? `${min} Min. ${sec}s` : `${sec}s`;
};

const KpiTile = (props: { value: string; label: string }) => {
	return (
		<div className="usage-kpi-tile">
			<div className="kpi-value">{props.value}</div>
			<div className="kpi-label">{props.label}</div>
		</div>
	);
};

// Folie "Allgemeine Nutzung": wichtige KPI im Überblick und Nutzung per Gerätetyp,
// Datenbasis Matomo, Zeitraum 1.1. bis heute.
export const UsageKpiCharts = observer(() => {
	const { adminTrackingStore } = useStore();
	const [data, setData] = useState<MatomoVisitsData | undefined>(undefined);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const res = await adminTrackingStore.getMatomoVisitsData(moment().startOf('year').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
				setData(res);
			} catch (e) {
				setData(undefined);
			}
			setLoading(false);
		};
		load();
	}, [adminTrackingStore]);

	if (loading) {
		return <div>Wird geladen...</div>;
	}
	if (!data) {
		return (
			<UsageSlide title="Allgemeine Nutzung" source={UsageSource.matomo}>
				<div className="note-box content">Matomo-Daten konnten nicht geladen werden.</div>
			</UsageSlide>
		);
	}

	// Anteile am Gesamtbesuchsaufkommen; Segmentbreite = Anteil
	const devices = data.devices.filter((d) => d.nb_visits > 0).sort((a, b) => b.nb_visits - a.nb_visits);
	const totalDeviceVisits = Math.max(1, devices.reduce((sum, d) => sum + d.nb_visits, 0));
	const devicePct = (d: { nb_visits: number }) => Math.round((d.nb_visits / totalDeviceVisits) * 100);

	return (
		<UsageSlide title="Allgemeine Nutzung" source={UsageSource.matomo}>
			<div className="usage-kpi-grid">
				<KpiTile value={formatNum(data.totalVisits)} label="Besuche" />
				<KpiTile value={formatDuration(data.averageTime)} label="Ø Aufenthaltsdauer" />
				<KpiTile value={`${data.actionsPerVisit}`} label="Aktionen pro Besuch" />
				<KpiTile value={formatNum(data.pageViews)} label="Σ Seitenansichten" />
			</div>
			<div className="usage-section-title">Nutzung per Gerätetyp</div>
			<div className="usage-device-split">
				<div className="device-bar">
					{devices.map((d, idx) => (
						<div key={d.label} className={`device-segment is-device-${idx}`} style={{ width: `${(d.nb_visits / totalDeviceVisits) * 100}%` }} />
					))}
				</div>
				<div className="device-legend">
					{devices.map((d, idx) => (
						<span key={d.label}>
							<i className={`legend-swatch is-device-${idx}`} /> {d.label}{' '}
							<strong>
								{formatNum(d.nb_visits)} ({devicePct(d)}%)
							</strong>
						</span>
					))}
				</div>
			</div>
			<div className="slide-footnote">
				Besuche mit Mehrfachzählung pro Tag. Aktionen umfassen Seitenansichten, Downloads und Ereignisse. Datenerhebung gemäß GBV DVS für VBL und
				GBV DVS für Spezialisten, LPV.
			</div>
		</UsageSlide>
	);
});
